/* Shopify Forms bridge with native contact fallback.
   Supports forms with or without product selection. */

if (!customElements.get('rental-request-form')) {
  customElements.define('rental-request-form', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      this.choices = [...this.querySelectorAll('[data-rental-choice]')];
      this.error = this.querySelector('[data-product-error]');

      const form = this.querySelector('.rental-form__native');

      if (!form) return;

      this.form = form;
      this.shopifyContainer = this.querySelector('[data-rental-shopify-form]');
      this.submitButton = form.querySelector('[type="submit"]');
      this.appStatus = this.querySelector('[data-rental-app-status]');
      this.appSuccess = this.querySelector('[data-rental-app-success]');
      this.productGrid = this.querySelector('.rental-form__product-grid');

      if (
        this.hasAttribute('data-sync-rental-products') &&
        this.productGrid
      ) {
        if (!this.fallbackCards) {
          this.fallbackCards = [...this.productGrid.children].map(
            card => card.cloneNode(true)
          );
        }

        this.syncProducts();

        document.addEventListener(
          'shopify:section:load',
          () => this.syncProducts(),
          { signal }
        );

        document.addEventListener(
          'shopify:section:unload',
          event => {
            if (event.target.querySelector?.('#rental-equipment')) {
              this.syncProducts(event.target);
            }
          },
          { signal }
        );

        document.addEventListener(
          'shopify:block:select',
          () => this.syncProducts(),
          { signal }
        );
      }

      this.validateSelection(false);

      form.addEventListener(
        'change',
        () => this.validateSelection(false),
        { signal }
      );

      form.addEventListener(
        'input',
        () => this.syncShopifyFields(),
        { signal }
      );

      this.submitButton?.addEventListener(
        'click',
        () => this.validateSelection(true),
        { signal }
      );

      form.addEventListener(
        'invalid',
        event => {
          if (this.choices.includes(event.target)) {
            this.validateSelection(true);
          }
        },
        { signal, capture: true }
      );

      form.addEventListener(
        'submit',
        event => {
          if (this.shopifyContainer) {
            event.preventDefault();
          }

          if (!this.validateSelection(true)) {
            event.preventDefault();
            this.choices[0]?.focus();
            return;
          }

          if (this.shopifyContainer && form.reportValidity()) {
            this.submitToShopify();
          }
        },
        { signal }
      );

      document.addEventListener(
        'click',
        event => {
          const link = event.target.closest('[data-rental-product]');

          if (!link || !this.closest('#rental-request')) return;

          const choice = this.choices.find(
            input => input.dataset.productId === link.dataset.rentalProduct
          );

          if (choice) {
            choice.checked = true;
            this.validateSelection(false);
          }
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    syncProducts(excludedSection) {
      const selected = new Set(
        this.choices
          .filter(input => input.checked && input.dataset.productId)
          .map(input => input.dataset.productId)
      );

      const seen = new Set();

      const options = [
        ...document.querySelectorAll(
          '#rental-equipment template[data-rental-product-option]'
        )
      ].filter(option => {
        const id = option.dataset.productId;

        if (excludedSection?.contains(option) || seen.has(id)) {
          return false;
        }

        seen.add(id);
        return true;
      });

      if (!options.length && !this.hasSyncedProducts) return;

      const contactId = this.dataset.contactId;

      const cards = options.length
        ? options.map((option, index) => {
            const card = option.content.firstElementChild.cloneNode(true);
            const input = card.querySelector('[data-rental-choice]');

            input.id = `${contactId}-product-${option.dataset.productId}`;
            input.name = `contact[Produs ${index + 1}]`;

            input.setAttribute(
              'aria-describedby',
              `${contactId}-product-help ${contactId}-product-error`
            );

            input.checked =
              selected.has(option.dataset.productId) ||
              option.dataset.productId === this.dataset.selectedProductId;

            card.htmlFor = input.id;

            return card;
          })
        : this.fallbackCards.map(card => card.cloneNode(true));

      this.productGrid.replaceChildren(...cards);
      this.hasSyncedProducts = options.length > 0;

      this.choices = [
        ...this.productGrid.querySelectorAll('[data-rental-choice]')
      ];

      this.validateSelection(false);
    }

    validateSelection(showError) {
      const valid =
        this.choices.length === 0 ||
        this.choices.some(input => input.checked);

      this.choices[0]?.setCustomValidity(
        valid ? '' : 'Selectează cel puțin un echipament.'
      );

      this.choices.forEach(input => {
        if (!valid && showError) {
          input.setAttribute('aria-invalid', 'true');
        } else {
          input.removeAttribute('aria-invalid');
        }
      });

      if (this.error) {
        this.error.hidden = valid || !showError;
      }

      this.syncShopifyFields();

      return valid;
    }

    getShopifyTarget() {
      if (!this.shopifyContainer) return null;

      const roots = [this.shopifyContainer];

      for (let index = 0; index < roots.length; index++) {
        const root = roots[index];

        for (const element of root.querySelectorAll('*')) {
          if (element.shadowRoot) {
            roots.push(element.shadowRoot);
          }
        }

        const fields = {
          company: root.querySelector('[id="first_name"]'),
          email: root.querySelector('[id="email"]'),
          phone: root.querySelector('[id="custom#phone"]'),
          product: root.querySelector('[id="custom#product"]'),
          period: root.querySelector('[id="custom#period"]'),
          message: root.querySelector('textarea[name="custom#message"]')
        };

        const requiredKeys = [
          'company',
          'email',
          'phone',
          'period',
          'message'
        ];

        if (requiredKeys.some(key => !fields[key])) continue;

        const form = fields.email.closest('form');

        const scope =
          form ||
          fields.email.closest('[role="form"]') ||
          root;

        const button =
          scope.querySelector('button[type="submit"]') ||
          scope.querySelector('button');

        if (button) {
          return { root, fields, form, button };
        }
      }

      return null;
    }

    getPayload() {
      const value = name =>
        this.form.elements.namedItem(name)?.value.trim() || '';

      return {
        company: value('contact[Companie]'),
        email: value('contact[email]'),
        phone: value('contact[Telefon]'),
        period: value('contact[Perioada de inchiriere]'),
        message: value('contact[body]'),

        product: this.choices.length
          ? JSON.stringify(
              this.choices
                .filter(input => input.checked)
                .map(input => ({
                  title:
                    input.dataset.productTitle ||
                    input
                      .closest('label')
                      .querySelector('.rental-form__product-title')
                      .textContent.trim(),
                  id: input.dataset.productId || null
                }))
            )
          : ''
      };
    }

    syncShopifyFields(
      target = this.getShopifyTarget(),
      payload = this.getPayload(),
      force = false
    ) {
      if (!target || (this.isSubmitting && !force)) return;

      for (const [key, field] of Object.entries(target.fields)) {
        if (!field || field.value === payload[key]) continue;

        const descriptor = Object.getOwnPropertyDescriptor(
          Object.getPrototypeOf(field),
          'value'
        );

        if (descriptor?.set) {
          descriptor.set.call(field, payload[key]);
        } else {
          field.value = payload[key];
        }

        field.dispatchEvent(
          new Event('input', { bubbles: true, composed: true })
        );

        field.dispatchEvent(
          new Event('change', { bubbles: true, composed: true })
        );
      }
    }

    setAppStatus(message) {
      if (!this.appStatus) return;

      this.appStatus.textContent = message;
      this.appStatus.hidden = !message;
    }

    async waitForShopifyTarget(signal) {
      for (let attempt = 0; attempt < 50; attempt++) {
        signal.throwIfAborted();

        const target = this.getShopifyTarget();

        if (target) return target;

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      throw new Error(
        'Formularul nu este încă disponibil. Încearcă din nou.'
      );
    }

    async waitForShopifyResponse(target, signal) {
      for (let attempt = 0; attempt < 300; attempt++) {
        signal.throwIfAborted();

        const error = target.root.querySelector(
          '[role="alert"], [aria-invalid="true"]'
        );

        if (
          error &&
          !error.hidden &&
          (error.textContent.trim() || error.validationMessage)
        ) {
          throw new Error(
            error.validationMessage || error.textContent.trim()
          );
        }

        if (
          !target.root.querySelector(
            '[id="email"], textarea[name="custom#message"]'
          )
        ) {
          const response = [
            ...target.root.querySelectorAll(
              'h1, h2, h3, p, [role="status"]'
            )
          ]
            .filter(element => !element.hidden)
            .map(element => element.textContent.trim())
            .filter(Boolean)
            .join(' ');

          if (response) return response;
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      throw new Error(
        'Nu am putut confirma trimiterea. Contactează-ne pentru a verifica primirea cererii.'
      );
    }

    async submitToShopify() {
      if (this.isSubmitting) return;

      const payload = this.getPayload();
      const { signal } = this.abortController;

      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.setAppStatus('Se trimite cererea…');

      try {
        let target = await this.waitForShopifyTarget(signal);

        this.syncShopifyFields(target, payload, true);

        await new Promise(resolve => setTimeout(resolve, 0));

        signal.throwIfAborted();

        target = this.getShopifyTarget();

        if (!target) {
          throw new Error(
            'Formularul nu este încă disponibil. Încearcă din nou.'
          );
        }

        const invalid = Object.values(target.fields).find(
          field => field && !field.checkValidity()
        );

        if (
          invalid ||
          (target.form && !target.form.checkValidity())
        ) {
          throw new Error(
            invalid?.validationMessage ||
            'Verifică detaliile cererii.'
          );
        }

        if (target.button.disabled) {
          throw new Error(
            'Formularul nu poate primi cererea încă. Încearcă din nou.'
          );
        }

        target.button.click();

        const response = await this.waitForShopifyResponse(target, signal);

        this.querySelector('[data-rental-app-response]').textContent =
          response;

        this.form.hidden = true;
        this.appSuccess.hidden = false;
        this.appSuccess.focus();

        this.setAppStatus('');
      } catch (error) {
        if (!signal.aborted) {
          this.setAppStatus(error.message);
        }
      } finally {
        this.isSubmitting = false;
        this.submitButton.disabled = false;
        this.form.setAttribute('aria-busy', 'false');
      }
    }
  });
}